import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom-v5-compat';
import {
  VaFileInputMultiple,
  VaFileInput,
  VaButton,
  VaSelect,
  VaModal,
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  readAndCheckFile,
  checkIsEncryptedPdf,
} from 'platform/forms-system/src/js/utilities/file';
import { scrollToFirstError } from 'platform/utilities/scroll';
import { claimTabHref } from '../../utils/claim-tab-helpers';
import { DOC_TYPES, buildUploadHintText } from '../../utils/helpers';
import {
  FILE_TYPES,
  checkForDuplicates,
  isPdf,
  validateFiles,
} from '../../utils/validations';
import {
  BENEFITS_DOCUMENTS_API_NAME,
  CLAIMS_EVIDENCE_API_NAME,
  countInFlightRetries,
  generateBatchId,
  recordBlockedSubmitEvent,
  recordValidationErrors,
} from '../../utils/analytics';
import UploadStatus from '../UploadStatus';
import {
  ANCHOR_LINKS,
  CHAMPVA_HINT_TEXT,
  DOC_TYPE_ERROR,
  DUPLICATE_FILE_ERROR,
  LABEL_TEXT,
  PASSWORD_ERROR,
  SUBMIT_TEXT,
  SEND_YOUR_DOCUMENTS_TEXT,
  VALIDATION_ERROR,
} from '../../constants';
import { setPageFocus } from '../../utils/page';

// File encryption utilities
const checkFileEncryption = async file => {
  if (!isPdf(file)) {
    return false;
  }

  try {
    const checks = { checkIsEncryptedPdf };
    const checkResults = await readAndCheckFile(file, checks);
    return checkResults.checkIsEncryptedPdf;
  } catch (error) {
    return false;
  }
};

// Keeps only the entries at `indexes` (in list order), for the parallel
// files/errors/encrypted arrays. Used after a partial upload to keep the
// failed and cancelled files, with their doc type, password and error, for
// retry.
export const keepEntriesAtIndexes = (list, indexes) =>
  [...indexes].sort((a, b) => a - b).map(index => list[index]);

const createEncryptedFilesList = async files => {
  return Promise.all(
    files.map(async fileInfo => checkFileEncryption(fileInfo.file)),
  );
};

// Shadow DOM extraction utilities
const extractPasswordsFromShadowDOM = (fileInputRef, files, encrypted) => {
  const updatedFiles = [...files];
  const vaFileInputElements =
    fileInputRef.current?.shadowRoot?.querySelectorAll('va-file-input');

  if (vaFileInputElements) {
    vaFileInputElements.forEach((vaFileInput, index) => {
      if (encrypted[index]) {
        const vaTextInput =
          vaFileInput.shadowRoot?.querySelector('va-text-input');
        const passwordInput = vaTextInput?.shadowRoot?.querySelector('input');

        if (passwordInput && updatedFiles[index]) {
          updatedFiles[index] = {
            ...updatedFiles[index],
            password: passwordInput.value,
          };
        }
      }
    });
  }

  return updatedFiles;
};

const extractDocumentTypesFromShadowDOM = fileInputRef => {
  const fileInputs = Array.from(
    fileInputRef.current?.shadowRoot?.querySelectorAll('va-file-input') || [],
  );

  return fileInputs.map(fileInput => {
    const vaSelect = fileInput.querySelector('va-select');
    return vaSelect?.value || '';
  });
};

// Error handling utilities
const clearNoFilesError = prevErrors => {
  if (prevErrors.length === 1 && prevErrors[0] === VALIDATION_ERROR) {
    return [];
  }
  return prevErrors;
};

const clearSpecificErrors = (prevErrors, errorType, shouldClear) => {
  const newErrors = [...prevErrors];
  let hasChanges = false;

  prevErrors.forEach((error, index) => {
    if (error === errorType && shouldClear(index)) {
      newErrors[index] = null;
      hasChanges = true;
    }
  });

  return hasChanges ? newErrors : prevErrors;
};

const rebuildErrorsAfterFileDeletion = (currentFiles, newFiles, prevErrors) => {
  const newErrors = [];
  // Match errors to files by file reference, not by index
  newFiles.forEach((fileInfo, newIndex) => {
    const originalIndex = currentFiles.findIndex(f => f.file === fileInfo.file);
    if (originalIndex !== -1 && prevErrors[originalIndex]) {
      newErrors[newIndex] = prevErrors[originalIndex];
    }
  });
  return newErrors;
};

const updateErrorsOnFileChange = (prevErrors, files, newFiles, action) => {
  // First, clear "no files" error if present
  let updatedErrors = clearNoFilesError(prevErrors);

  // Handle errors based on the specific action
  if (action === 'FILE_REMOVED') {
    // Rebuild error array to match remaining files
    updatedErrors = rebuildErrorsAfterFileDeletion(files, newFiles, prevErrors);
  } else if (action === 'PASSWORD_UPDATE') {
    // Clear password errors when passwords are provided
    updatedErrors = clearSpecificErrors(
      updatedErrors,
      PASSWORD_ERROR,
      index =>
        newFiles[index]?.password && newFiles[index].password.trim() !== '',
    );
  }

  return updatedErrors;
};

const applyValidationErrors = (
  baseErrors,
  validationResults,
  files,
  wasFileReplaced = false,
) => {
  const updatedErrors = [...baseErrors];

  if (wasFileReplaced) {
    // Clear ALL errors when a file was replaced (FILE_UPDATED action) - fresh validation for replaced files
    files.forEach((_, index) => {
      updatedErrors[index] = null;
    });
  }

  // Apply new validation errors
  validationResults.forEach(result => {
    updatedErrors[result.index] = result.error;
  });

  return updatedErrors;
};

/**
 * Given an array of file info objects, checks first if there are errors from a previous submission
 * Then iterates through the files to validate whether:
 * 1) An encrypted file is missing a password
 * 2) A file is missing a document type (e.g. Buddy/Lay Statement)
 * 3) For Supplemental Claims only, checks if the file is a duplicate of an already uploaded file (in evidenceSubmissions)
 *
 * @param {Array} files - Array of file info objects
 * @param {Array} encrypted - Array of booleans indicating if each file is encrypted
 * @param {Array} docTypes - Array of document types corresponding to each file
 * @param {Array} existingErrors - Array of existing validation errors
 * @param {Array|null} evidenceSubmissions - Defaults to null. Populated or empty array of uploaded files for Supplemental Claims
 * @returns {Object} An object containing isValid (boolean) and errors (array)
 */
const validateFilesForSubmission = (
  files,
  encrypted,
  docTypes,
  existingErrors,
  evidenceSubmissions = null,
  usingClaimsEvidenceAPI = false,
) => {
  // Check if no files provided (always required)
  if (files.length === 0) {
    return { isValid: false, errors: [VALIDATION_ERROR] };
  }

  // Re-enable checking existing errors (file validation errors)
  const errors = [...(existingErrors || [])];
  // Which kind of duplicate each file was, by index. Both kinds share one error
  // message, so analytics can't tell them apart without this.
  const duplicateReasons = [];
  let hasErrors = false;

  files.forEach((fileInfo, index) => {
    // Check existing validation errors
    if (errors[index]) {
      hasErrors = true;
    }
    // Check if file is encrypted and missing password
    else if (
      encrypted[index] &&
      (!fileInfo.password || fileInfo.password.trim() === '')
    ) {
      errors[index] = PASSWORD_ERROR;
      hasErrors = true;
    }
    // Check if document type is missing or empty
    else if (!docTypes[index] || docTypes[index].trim() === '') {
      errors[index] = DOC_TYPE_ERROR;
      hasErrors = true;
    }

    if (usingClaimsEvidenceAPI) {
      const duplicateReason = checkForDuplicates(
        fileInfo.file,
        files,
        index,
        docTypes,
        evidenceSubmissions,
      );

      // Clears a duplicate error left over from a previous pass, now resolved.
      if (!duplicateReason && errors[index] === DUPLICATE_FILE_ERROR) {
        errors[index] = null;
      }

      // Also matches an error this file already carried, not just an empty
      // slot: a duplicate left unresolved is re-reported on every submit, and
      // without this the reason would only ride along on the first one. A
      // password or doc-type error still takes precedence, so `duplicate-reason`
      // never attaches to an event reporting a different key.
      if (
        duplicateReason &&
        (!errors[index] || errors[index] === DUPLICATE_FILE_ERROR)
      ) {
        errors[index] = DUPLICATE_FILE_ERROR;
        duplicateReasons[index] = duplicateReason;
        hasErrors = true;
      }
    }
  });

  // One flag for the whole batch so a later file doesn't overwrite
  // a result from an earlier one
  hasErrors = errors.some(Boolean);

  return {
    isValid: !hasErrors,
    errors: hasErrors ? errors : [],
    duplicateReasons,
  };
};

const createSubmissionPayload = (files, docTypes, encrypted) => {
  return files.map((fileInfo, index) => ({
    file: fileInfo.file,
    // Include file metadata for better debugging (File objects don't serialize)
    fileMetadata: fileInfo.file
      ? {
          name: fileInfo.file.name,
          size: fileInfo.file.size,
          type: fileInfo.file.type,
          lastModified: fileInfo.file.lastModified,
        }
      : null,
    password: encrypted[index] ? fileInfo.password : '',
    docType: docTypes[index] || '',
  }));
};

const AddFilesForm = ({
  acceptedFileTypes,
  applicantOptions,
  documentTypes,
  evidenceSubmissions = null,
  headerSize,
  isChampvaFlow,
  fileTab,
  useVaTabs = false,
  hideOtherWaysLink,
  hideRequiredIndicator,
  onSubmit,
  uploading,
  progress,
  onCancel,
  uploadResults = null,
  usingClaimsEvidenceAPI = false,
}) => {
  const { id: claimId } = useParams();
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);
  const [encrypted, setEncrypted] = useState([]);
  const [canShowUploadModal, setCanShowUploadModal] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState('');
  const [applicantSelectionError, setApplicantSelectionError] = useState('');
  const [singleDocumentType, setSingleDocumentType] = useState('');
  const [singleFileProcessing, setSingleFileProcessing] = useState(false);
  const fileInputRef = useRef(null);
  // Batch id for the current submit attempt. Used to join the attempt's events
  // across GA and DataDog.
  const batchIdRef = useRef(null);
  const singleFileChangeIdRef = useRef(0);
  const removingSucceededFilesRef = useRef(false);
  const handledUploadResultsRef = useRef(null);
  const selectOptions = documentTypes?.length ? documentTypes : DOC_TYPES;
  const fileTypes = acceptedFileTypes?.length ? acceptedFileTypes : FILE_TYPES;
  const validationApiName = usingClaimsEvidenceAPI
    ? CLAIMS_EVIDENCE_API_NAME
    : BENEFITS_DOCUMENTS_API_NAME;
  let uploadHintText = buildUploadHintText(undefined, usingClaimsEvidenceAPI);

  if (isChampvaFlow) {
    uploadHintText = CHAMPVA_HINT_TEXT;
  } else if (acceptedFileTypes?.length) {
    uploadHintText = buildUploadHintText(fileTypes, usingClaimsEvidenceAPI);
  }

  // Build the href for "other ways to send documents" link
  // When on the files tab, use anchor link; otherwise use full path
  const otherWaysAnchor = `#${ANCHOR_LINKS.otherWaysToSendDocuments}`;
  let otherWaysToSendHref = `/track-claims/your-claims/${claimId}/files${otherWaysAnchor}`;
  if (fileTab) {
    otherWaysToSendHref = otherWaysAnchor;
  } else if (useVaTabs) {
    otherWaysToSendHref = claimTabHref(claimId, 'files', {
      hash: otherWaysAnchor,
    });
  }

  // Track document type changes and clear errors immediately
  useEffect(() => {
    if (isChampvaFlow) {
      return undefined;
    }

    // Poll for document type changes to clear errors immediately
    const interval = setInterval(() => {
      const currentDocTypes = extractDocumentTypesFromShadowDOM(fileInputRef);

      setErrors(prevErrors =>
        clearSpecificErrors(
          prevErrors,
          DOC_TYPE_ERROR,
          index => currentDocTypes[index]?.trim() !== '',
        ),
      );
    }, 150);

    return () => clearInterval(interval);
  }, [files, isChampvaFlow]);

  // After a batch where some files fail, remove the ones that succeeded so
  // only the failed files are left to retry
  useEffect(() => {
    if (
      !uploadResults ||
      uploading ||
      handledUploadResultsRef.current === uploadResults
    ) {
      return;
    }
    handledUploadResultsRef.current = uploadResults;

    // Only files reported as uploaded are removed. Failed and cancelled files
    // stay for retry.
    const { succeededIndexes, fileCount } = uploadResults;
    const succeeded = new Set(succeededIndexes);
    const keptIndexes = files
      .map((_, index) => index)
      .filter(index => !succeeded.has(index));

    // Skip if nothing uploaded, or the list changed since it was submitted
    if (!succeededIndexes.length || files.length !== fileCount) {
      return;
    }

    removingSucceededFilesRef.current = true;
    Promise.resolve(fileInputRef.current?.removeFiles?.(succeededIndexes))
      .catch(() => {})
      .finally(() => {
        removingSucceededFilesRef.current = false;
      });

    setFiles(current => keepEntriesAtIndexes(current, keptIndexes));
    setErrors(current => keepEntriesAtIndexes(current, keptIndexes));
    setEncrypted(current => keepEntriesAtIndexes(current, keptIndexes));
  }, [uploadResults, uploading, files]);

  const handleFileChange = async event => {
    const { action, state, index } = event.detail;
    if (action === 'FILE_REMOVED' && removingSucceededFilesRef.current) {
      return;
    }
    const newFiles = state || [];

    // Validate all files
    const validationResults = await validateFiles(
      newFiles,
      usingClaimsEvidenceAPI,
    );

    // Record validation errors to DataDog, tagged phase 'add'. Filter to the
    // changed file (event index): validateFiles re-checks the whole list, and
    // the guard skips FILE_REMOVED/PASSWORD_UPDATE, so unchanged files'
    // already-recorded errors don't re-fire on every add or keystroke.
    if (action === 'FILE_ADDED' || action === 'FILE_UPDATED') {
      recordValidationErrors(
        validationResults
          .filter(result => result.index === index)
          .map(result => result.error),
        newFiles.length,
        'add',
        validationApiName,
      );
    }

    setFiles(newFiles);

    if (action === 'FILE_ADDED') {
      // Return focus to the file input after file picker closes.
      // On iOS VoiceOver, the browser drops focus to the top of the page
      // when the file picker dismisses — explicitly restoring focus here
      // keeps the user in context.
      fileInputRef.current?.focus();
    }

    if (newFiles.length > 0) {
      // Update errors based on file changes and validation
      setErrors(prevErrors => {
        const baseErrors = updateErrorsOnFileChange(
          prevErrors,
          files,
          newFiles,
          action,
        );

        // Check if a file was replaced (not added or removed)
        const wasFileReplaced = action === 'FILE_UPDATED';
        return applyValidationErrors(
          baseErrors,
          validationResults,
          newFiles,
          wasFileReplaced,
        );
      });

      const encryptedStatus = await createEncryptedFilesList(newFiles);
      setEncrypted(encryptedStatus);
    } else {
      // Clear all errors when all files are removed
      setErrors([]);
      setEncrypted([]);
    }
  };

  const handleSingleFileChange = async event => {
    const file = event.detail.files?.[0];
    const newFiles = file ? [{ file }] : [];
    const changeId = singleFileChangeIdRef.current + 1;
    singleFileChangeIdRef.current = changeId;

    setFiles(newFiles);
    setErrors([]);
    setEncrypted([]);
    setSingleFileProcessing(!!file);

    if (!file) {
      return;
    }

    const validationResults = await validateFiles(newFiles, false);
    if (changeId !== singleFileChangeIdRef.current) {
      return;
    }

    recordValidationErrors(
      validationResults.map(result => result.error),
      1,
      'add',
    );
    setErrors(applyValidationErrors([], validationResults, newFiles, true));

    const encryptedStatus = await createEncryptedFilesList(newFiles);
    if (changeId !== singleFileChangeIdRef.current) {
      return;
    }

    setEncrypted(encryptedStatus);
    setSingleFileProcessing(false);
  };

  const handleSingleFileError = event => {
    singleFileChangeIdRef.current += 1;
    setFiles([]);
    setErrors([event.detail.error]);
    setEncrypted([]);
    setSingleFileProcessing(false);
  };

  const handleSingleFilePasswordChange = event => {
    const password = event.detail.password || '';
    setFiles(currentFiles =>
      currentFiles.map(fileInfo => ({ ...fileInfo, password })),
    );
    if (password.trim()) {
      setErrors(currentErrors =>
        currentErrors[0] === PASSWORD_ERROR ? [] : currentErrors,
      );
    }
  };

  const handleSubmit = () => {
    if (isChampvaFlow && singleFileProcessing) {
      return;
    }

    if (isChampvaFlow && applicantOptions.length > 0 && !selectedApplicant) {
      setApplicantSelectionError('Select an applicant');
      // VADS: "When an error blocks the next action, move focus to the first
      // input with an error."
      // https://design.va.gov/accessibility/focus-management#when-there-is-an-error-on-the-page
      // va-radio and va-select announce their error through a role="alert"
      // region but neither moves focus, so without this the user is left on the
      // submit button at the bottom of the form.
      //
      // Deferred a tick because scrollToFirstError runs an immediate
      // queryForErrors() before its MutationObserver takes over, and focusing a
      // match disconnects the observer. Called synchronously it would find a
      // leftover file error from an earlier attempt, focus that, and stop --
      // the applicant error renders on the next tick and would never be seen.
      setTimeout(() => scrollToFirstError());
      return;
    }

    // One id per submit attempt, generated before validation so an attempt
    // blocked here — which never reaches the uploader — still has one. Held in a
    // ref so the cancel handler, rendered separately, reports the same attempt.
    const batchId = generateBatchId();
    batchIdRef.current = batchId;

    // Extract current password values from shadow DOM elements
    const updatedFiles = isChampvaFlow
      ? files
      : extractPasswordsFromShadowDOM(fileInputRef, files, encrypted);

    // Extract document types from shadow DOM
    const currentDocTypes = isChampvaFlow
      ? [singleDocumentType]
      : extractDocumentTypesFromShadowDOM(fileInputRef);

    // Pass existing errors to validation so ALL errors are checked together
    const validation = validateFilesForSubmission(
      updatedFiles,
      encrypted,
      currentDocTypes,
      errors,
      evidenceSubmissions,
      usingClaimsEvidenceAPI,
    );

    if (!validation.isValid) {
      // Report each blocking validation error to DataDog so the dashboard can
      // distinguish failure causes (missing password, missing doc type, file
      // too large, etc.). Tagged phase 'submit' to avoid double-counting
      // errors already reported at 'add' time.
      recordValidationErrors(
        validation.errors,
        updatedFiles.length,
        'submit',
        validationApiName,
        { duplicateReasons: validation.duplicateReasons, batchId },
      );

      // For Supplemental Claims only, we show blocked uploads before the request was made
      if (usingClaimsEvidenceAPI) {
        recordBlockedSubmitEvent({
          errorMessages: validation.errors,
          fileCount: updatedFiles.length,
          batchId,
          apiName: validationApiName,
        });
      }
      setErrors(validation.errors);

      // Doc-type only. va-file-input is handed `undefined` for that error, so
      // its own watcher stays quiet and the error surfaces on the va-select
      // below it with nothing moving focus. Every other error does reach the
      // file input, which focuses itself -- calling this for those too would
      // put two movers on the same submit, either stealing focus back after the
      // user starts fixing the field or announcing the error twice.
      if (isChampvaFlow && validation.errors[0] === DOC_TYPE_ERROR) {
        setTimeout(() => scrollToFirstError());
      }

      return;
    }

    // Create complete payload for submission
    const payload = createSubmissionPayload(
      updatedFiles,
      currentDocTypes,
      encrypted,
    );

    // The display name for the selected id, resolved from the options we just
    // rendered. Used only by the local upload success alert; see below.
    const selectedApplicantName =
      applicantOptions.find(applicant => applicant.id === selectedApplicant)
        ?.name || '';

    // Create files array in the exact format the submit actions expect
    const formattedFiles = payload.map(item => ({
      file: item.file,
      docType: { value: item.docType },
      password: { value: item.password },
      // selectedApplicant holds the shared applicant id. applicantName is carried
      // alongside it for the local success alert only -- it is never submitted.
      // The backend resolves the name it stores from the id.
      applicantId:
        isChampvaFlow && selectedApplicant ? selectedApplicant : null,
      applicants:
        isChampvaFlow && selectedApplicantName ? [selectedApplicantName] : [],
    }));

    // Show upload modal on successful validation
    setCanShowUploadModal(true);
    onSubmit(formattedFiles, batchId);
  };

  const showUploadModal = uploading && canShowUploadModal;

  return (
    <div
      className={`add-files-form${
        headerSize ? ' add-files-form--heading-label' : ''
      }`}
    >
      <div>
        {!fileTab && (
          <>
            <h2>Upload documents</h2>
            <p>If you have a document to upload, you can do that here.</p>
          </>
        )}
      </div>
      {isChampvaFlow && applicantOptions.length > 0 && (
        <VaRadio
          className="vads-u-margin-bottom--3"
          label="Which applicant is this document for?"
          required
          error={applicantSelectionError || null}
          onVaValueChange={event => {
            setSelectedApplicant(event.detail.value);
            if (applicantSelectionError) {
              setApplicantSelectionError('');
            }
          }}
        >
          {applicantOptions.map(applicant => (
            <VaRadioOption
              key={applicant.id}
              label={applicant.name}
              name="applicant"
              value={applicant.id}
              checked={selectedApplicant === applicant.id}
            />
          ))}
        </VaRadio>
      )}
      {isChampvaFlow ? (
        <>
          <VaFileInput
            accept={fileTypes.map(type => `.${type}`).join(',')}
            ref={fileInputRef}
            headerSize={headerSize}
            hint={uploadHintText}
            label="Upload file"
            required={!hideRequiredIndicator}
            onVaChange={handleSingleFileChange}
            onVaFileInputError={handleSingleFileError}
            onVaPasswordChange={handleSingleFilePasswordChange}
            error={errors[0] === DOC_TYPE_ERROR ? undefined : errors[0]}
            encrypted={encrypted[0] || false}
            data-dd-privacy="mask"
            data-dd-action-name="file upload input"
            disablePasswordSubmitButtonPattern
          />
          <VaSelect
            required
            name="docType"
            label="What type of document is this?"
            value={singleDocumentType}
            error={errors[0] === DOC_TYPE_ERROR ? DOC_TYPE_ERROR : undefined}
            onVaSelect={event => {
              setSingleDocumentType(event.detail.value);
              if (errors[0] === DOC_TYPE_ERROR) {
                setErrors([]);
              }
            }}
          >
            {selectOptions.map(doc => (
              <option key={doc.value} value={doc.value}>
                {doc.label}
              </option>
            ))}
          </VaSelect>
        </>
      ) : (
        <VaFileInputMultiple
          accept={fileTypes.map(type => `.${type}`).join(',')}
          ref={fileInputRef}
          headerSize={headerSize}
          hint={uploadHintText}
          label={LABEL_TEXT}
          required={!hideRequiredIndicator}
          onVaMultipleChange={handleFileChange}
          errors={errors}
          encrypted={encrypted}
          data-dd-privacy="mask"
          data-dd-action-name="file upload input"
          disablePasswordSubmitButtonPattern
        >
          <VaSelect
            required
            name="docType"
            label="What type of document is this?"
          >
            {selectOptions.map(doc => (
              <option key={doc.value} value={doc.value}>
                {doc.label}
              </option>
            ))}
          </VaSelect>
        </VaFileInputMultiple>
      )}
      <VaButton
        class="vads-u-margin-top--3"
        text={SUBMIT_TEXT}
        {...(isChampvaFlow && singleFileProcessing ? { disabled: true } : {})}
        onClick={handleSubmit}
      />
      {!hideOtherWaysLink && (
        <div className="vads-u-margin-top--3 vads-u-margin-bottom--5">
          <va-link
            href={otherWaysToSendHref}
            text={SEND_YOUR_DOCUMENTS_TEXT}
            onClick={e => {
              // Only prevent default and scroll if we're on the files tab
              // Otherwise, let the link navigate to the files page
              if (fileTab) {
                e.preventDefault();
                setPageFocus(`#${ANCHOR_LINKS.otherWaysToSendDocuments}`);
              }
            }}
          />
        </div>
      )}
      <VaModal
        id="upload-status"
        modalTitle="Uploading files"
        onCloseEvent={() => setCanShowUploadModal(false)}
        visible={showUploadModal}
      >
        <UploadStatus
          progress={progress}
          files={files.length}
          onCancel={() => {
            onCancel({
              cancelFileCount: files.length,
              retryFileCount: countInFlightRetries(files, claimId),
              // Only Supplemental Claims needs a batch ID for upload events
              batchId: usingClaimsEvidenceAPI ? batchIdRef.current : undefined,
            });
          }}
        />
      </VaModal>
    </div>
  );
};

AddFilesForm.propTypes = {
  progress: PropTypes.number.isRequired,
  uploading: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
  applicantOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  documentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  evidenceSubmissions: PropTypes.arrayOf(
    PropTypes.shape({
      createdAt: PropTypes.string,
      documentType: PropTypes.string,
      fileName: PropTypes.string,
      fileSize: PropTypes.number,
      id: PropTypes.string,
      uploadStatus: PropTypes.string,
    }),
  ),
  fileTab: PropTypes.bool,
  // Renders the widget's label as an h1-h6 instead of a <label>, so callers
  // don't have to render a heading of their own and duplicate the text.
  headerSize: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  hideOtherWaysLink: PropTypes.bool,
  // Drops the "(*Required)" indicator, and with it the spoken "required" in
  // the input's accessible name — deliberately both, so sighted and
  // screen-reader users get the same picture. Purely presentational: file
  // validation runs via validateFilesForSubmission either way.
  hideRequiredIndicator: PropTypes.bool,
  isChampvaFlow: PropTypes.bool,
  uploadResults: PropTypes.shape({
    succeededIndexes: PropTypes.arrayOf(PropTypes.number).isRequired,
    fileCount: PropTypes.number.isRequired,
  }),
  useVaTabs: PropTypes.bool,
  usingClaimsEvidenceAPI: PropTypes.bool,
};

AddFilesForm.defaultProps = {
  applicantOptions: [],
  isChampvaFlow: false,
};

export default AddFilesForm;
